import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, GripVertical, Trash2, Save, ImageIcon, Video, Code, Tag, ShoppingCart, Cloud, Clock, Check, Shuffle, Trophy, ChevronDown, Settings2, CalendarClock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient } from '@/services/api';
import { SlideEditor } from './SlideEditor';
import { parseSchedule, describeSchedule, scheduleState, isEmptySchedule } from '@/lib/slideSchedule';

const SLIDE_TYPES = [
  { value: 'image', label: 'Imagem', icon: ImageIcon },
  { value: 'video', label: 'Vídeo', icon: Video },
  { value: 'price_table', label: 'Tabela de preços', icon: Tag },
  { value: 'promo_banner', label: 'Banner de promoção', icon: Tag },
  { value: 'product_carousel', label: 'Carrossel de produtos', icon: ShoppingCart },
  { value: 'html', label: 'HTML custom', icon: Code },
  { value: 'weather', label: 'Clima', icon: Cloud },
  { value: 'datetime', label: 'Data/hora', icon: Clock },
  { value: 'football', label: 'Futebol — Brasileirão', icon: Trophy },
];

export function PlaylistEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isNew = !id || id === 'new';

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [themeId, setThemeId] = useState<number | null>(null);
  const [orientation, setOrientation] = useState<'landscape' | 'portrait'>('landscape');
  const [staggerOrder, setStaggerOrder] = useState(false);
  const [slides, setSlides] = useState<any[]>([]);
  const [selectedSlideId, setSelectedSlideId] = useState<number | null>(null);
  // Config da playlist (tema/orientação/rotação) é ajustada uma vez e depois
  // raramente revisitada — mantemos recolhida por padrão pra sobrar altura
  // pra lista de slides, que é o que se mexe o dia todo.
  const [settingsOpen, setSettingsOpen] = useState(false);

  const { data: playlist } = useQuery({
    queryKey: ['playlist', id],
    queryFn: () => apiClient.getPlaylist(Number(id)).then(r => r.data),
    enabled: !isNew,
  });
  const { data: themes = [] } = useQuery({
    queryKey: ['themes'],
    queryFn: () => apiClient.getThemes().then(r => r.data),
  });

  useEffect(() => {
    if (playlist) {
      setName(playlist.name);
      setDescription(playlist.description || '');
      setThemeId(playlist.theme_id);
      // API serializa o enum TvOrientation como "Landscape"/"Portrait" (PascalCase).
      // Normalizamos pra lowercase pra simplificar comparações no JSX e no payload.
      const o = (playlist.orientation ?? 'landscape').toString().toLowerCase();
      setOrientation(o === 'portrait' ? 'portrait' : 'landscape');
      setStaggerOrder(!!playlist.stagger_order);
      setSlides(playlist.slides || []);
    }
  }, [playlist]);

  const createMut = useMutation({
    mutationFn: () => apiClient.createPlaylist({ name, description, theme_id: themeId, orientation }),
    onSuccess: (r) => navigate(`/playlists/${r.data.id}`),
  });
  const updateMut = useMutation({
    mutationFn: () => apiClient.updatePlaylist(Number(id), { name, description, theme_id: themeId, orientation, stagger_order: staggerOrder }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['playlist', id] }); toast.success('Playlist salva'); },
  });
  const addSlideMut = useMutation({
    mutationFn: (type: string) => apiClient.createSlide({
      playlist_id: Number(id),
      type,
      content: getDefaultContent(type),
      // Defaults pensados em tempo de leitura confortável (DOOH):
      // - vídeo: 30s (clipe padrão)
      // - tabela/carrossel: 30s (vários produtos pra varrer)
      // - demais: 15s (mínimo do sistema)
      duration_ms: type === 'video' ? 30000
        : (type === 'price_table' || type === 'product_carousel') ? 30000
        : 15000,
    }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['playlist', id] });
      setSelectedSlideId(r.data.id);
    },
  });
  const reorderMut = useMutation({
    mutationFn: (orderedIds: number[]) => apiClient.reorderSlides(Number(id), orderedIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['playlist', id] }),
  });
  const delSlideMut = useMutation({
    mutationFn: (slideId: number) => apiClient.deleteSlide(slideId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['playlist', id] }); toast.success('Slide removido'); },
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  function onDragEnd(e: DragEndEvent) {
    if (!e.over || e.active.id === e.over.id) return;
    const oldIndex = slides.findIndex(s => s.id === e.active.id);
    const newIndex = slides.findIndex(s => s.id === e.over!.id);
    const newOrder = arrayMove(slides, oldIndex, newIndex);
    setSlides(newOrder);
    reorderMut.mutate(newOrder.map(s => s.id));
  }

  if (isNew) {
    return (
      <div className="container-app py-8 max-w-2xl">
        <Link to="/playlists" className="inline-flex items-center gap-1 text-sm text-muted-foreground mb-4 hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Voltar
        </Link>
        <h1 className="text-3xl font-bold">Nova playlist</h1>
        <p className="text-muted-foreground mt-1">Configure o básico — depois você adiciona os slides.</p>

        <div className="mt-8 space-y-6">
          <div>
            <label className="text-sm font-semibold mb-2 block">Nome *</label>
            <Input placeholder="Ex.: Cardápio do dia" value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-semibold mb-2 block">Descrição</label>
            <Input placeholder="Opcional" value={description} onChange={e => setDescription(e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-semibold mb-2 block">Tema visual</label>
            <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={themeId || ''} onChange={e => setThemeId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">Sem tema</option>
              {themes.map((t: any) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>

          {/* Orientação — decisão importante, com cartões grandes pra ficar claro */}
          <div>
            <label className="text-sm font-semibold mb-1 block">Como a TV vai ficar instalada?</label>
            <p className="text-xs text-muted-foreground mb-3">
              Defina antes de montar os slides — o conteúdo é desenhado pra essa orientação.
            </p>
            <OrientationPicker value={orientation} onChange={setOrientation} size="lg" />
          </div>

          <Button onClick={() => createMut.mutate()} loading={createMut.isPending} disabled={!name} className="w-full" variant="gradient" size="lg">
            Criar e abrir editor
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {/* Sidebar - lista de slides */}
      <div className="w-80 border-r border-border bg-card flex flex-col">
        <div className="p-3 border-b border-border space-y-1.5">
          <Link to="/playlists" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-3 w-3" /> Playlists
          </Link>
          <Input value={name} onChange={e => setName(e.target.value)} className="font-bold text-base h-9" placeholder="Nome da playlist" />

          {/* Resumo compacto — expande pra editar tema/orientação/rotação.
              Fica fechado por padrão: são ajustes raros, não precisam competir
              com a lista de slides pelo espaço vertical. */}
          <button
            type="button"
            onClick={() => setSettingsOpen(o => !o)}
            aria-expanded={settingsOpen}
            className="w-full flex items-center gap-2 rounded-md border border-input bg-background px-2.5 py-1.5 text-xs hover:bg-muted/50 transition-colors"
          >
            <Settings2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="flex-1 min-w-0 truncate text-left text-muted-foreground">
              {themes.find((t: any) => t.id === themeId)?.name || 'Sem tema'} · {orientation === 'portrait' ? 'Em pé' : 'Deitada'}
              {staggerOrder ? ' · Rotação' : ''}
            </span>
            <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${settingsOpen ? 'rotate-180' : ''}`} />
          </button>

          {settingsOpen && (
            <div className="space-y-2 pt-1">
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase mb-1 block">Tema</label>
                <select
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  value={themeId ?? ''}
                  onChange={e => setThemeId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">Sem tema</option>
                  {themes.map((t: any) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                <Link to="/themes" className="text-xs text-brand-600 hover:underline mt-1 inline-block">Ver galeria de temas →</Link>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase mb-1 block">Orientação da TV</label>
                <OrientationPicker value={orientation} onChange={setOrientation} size="sm" />
              </div>
              <StaggerToggle value={staggerOrder} onChange={setStaggerOrder} />
              <Button onClick={() => updateMut.mutate()} size="sm" className="w-full" loading={updateMut.isPending}>
                <Save className="h-3 w-3" /> Salvar info
              </Button>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-3 min-h-0">
          <div className="text-xs font-semibold text-muted-foreground uppercase mb-2 px-2">Slides ({slides.length})</div>
          {slides.length > 0 && slides.every((s: any) => !isEmptySchedule(parseSchedule(s.schedule))) && (
            <div className="flex gap-2 mb-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>Todos os slides são agendados — nos dias sem nenhum no ar a TV fica sem conteúdo. Deixe ao menos um slide fixo.</span>
            </div>
          )}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={slides.map(s => s.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-1">
                {slides.map((s) => (
                  <SortableSlideRow
                    key={s.id}
                    slide={s}
                    selected={selectedSlideId === s.id}
                    onClick={() => setSelectedSlideId(s.id)}
                    onDelete={() => confirm('Remover slide?') && delSlideMut.mutate(s.id)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>

        <div className="border-t border-border p-2">
          <div className="text-xs font-semibold text-muted-foreground uppercase mb-1.5 px-1">Adicionar slide</div>
          <div className="grid grid-cols-2 gap-1.5 px-1 pb-1">
            {SLIDE_TYPES.map((t) => (
              <Button
                key={t.value}
                variant="outline"
                size="sm"
                onClick={() => addSlideMut.mutate(t.value)}
                title={t.label}
                className="h-8 items-center justify-start gap-1.5 px-2 text-left"
              >
                <t.icon className="h-3.5 w-3.5 shrink-0" /> <span className="min-w-0 truncate text-xs">{t.label}</span>
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Editor central */}
      <div className="flex-1 overflow-y-auto bg-muted/30 flex flex-col">
        {/* Banner de orientação — fica fixo no topo pro usuário não esquecer
            que tipo de tela está configurando. Cor diferenciada por orientação. */}
        <div className={`flex items-center gap-3 px-4 py-2 border-b text-sm font-medium ${
          orientation === 'portrait'
            ? 'bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200'
            : 'bg-sky-50 border-sky-200 text-sky-900 dark:bg-sky-950/40 dark:text-sky-200'
        }`}>
          <TvShape orientation={orientation} className="h-5 w-5 shrink-0" />
          <span>
            Editando para TV <strong>{orientation === 'portrait' ? 'em pé (vertical)' : 'deitada (horizontal)'}</strong>
          </span>
          <span className="text-xs opacity-70 ml-1">
            {orientation === 'portrait' ? 'proporção 9:16 — 1080×1920' : 'proporção 16:9 — 1920×1080'}
          </span>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {selectedSlideId ? (
            <SlideEditor slideId={selectedSlideId} themeId={themeId} orientation={orientation} />
          ) : (
            <div className="h-full grid place-items-center text-muted-foreground">
              Selecione um slide ou adicione um novo →
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Toggle "Variar ordem entre TVs" (modo desencontrado / rotação).
// Quando ligado, cada TV que toca esta playlist começa por um slide
// diferente — telas lado a lado não exibem o mesmo conteúdo ao mesmo tempo.
// A rotação é calculada no servidor; aqui só guardamos a intenção.
function StaggerToggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      onClick={() => onChange(!value)}
      title="Cada TV começa por um slide diferente — telas lado a lado não repetem o mesmo conteúdo ao mesmo tempo."
      className={`w-full flex items-center gap-2 rounded-md border px-2.5 py-2 text-left transition-colors ${
        value
          ? 'border-brand-300 bg-brand-50 dark:border-brand-700 dark:bg-brand-950/40'
          : 'border-input bg-background hover:bg-muted/50'
      }`}
    >
      <Shuffle className={`h-3.5 w-3.5 shrink-0 ${value ? 'text-brand-600' : 'text-muted-foreground'}`} />
      <span className="flex-1 min-w-0 truncate text-xs font-semibold">Variar ordem entre TVs</span>
      {/* Trilho do switch */}
      <span className={`h-5 w-9 shrink-0 rounded-full p-0.5 transition-colors ${value ? 'bg-brand-600' : 'bg-muted-foreground/30'}`}>
        <span className={`block h-4 w-4 rounded-full bg-white shadow transition-transform ${value ? 'translate-x-4' : ''}`} />
      </span>
    </button>
  );
}

// Ícone SVG de TV (com base/pedestal) que rotaciona sob portrait.
// Inline pra não depender de lucide ter um par exato — assim o cartão
// fica inequívoco visualmente (TV deitada vs em pé).
function TvShape({ orientation, className }: { orientation: 'landscape' | 'portrait'; className?: string }) {
  const isPortrait = orientation === 'portrait';
  // Em portrait giramos 90° o mesmo desenho.
  return (
    <svg viewBox="0 0 64 64" className={className} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <g transform={isPortrait ? 'rotate(90 32 32)' : ''}>
        <rect x="6" y="12" width="52" height="32" rx="3" />
        <line x1="22" y1="52" x2="42" y2="52" />
        <line x1="32" y1="44" x2="32" y2="52" />
      </g>
    </svg>
  );
}

function OrientationPicker({
  value, onChange, size = 'md',
}: {
  value: 'landscape' | 'portrait';
  onChange: (v: 'landscape' | 'portrait') => void;
  size?: 'sm' | 'md' | 'lg';
}) {
  const compact = size === 'sm';
  const pad = compact ? 'p-2.5' : size === 'lg' ? 'p-5' : 'p-4';
  const iconSize = compact ? 'h-7 w-7' : size === 'lg' ? 'h-14 w-14' : 'h-10 w-10';
  const labelSize = compact ? 'text-xs' : 'text-sm';

  const opts: { val: 'landscape' | 'portrait'; label: string; hint: string }[] = [
    { val: 'landscape', label: 'Deitada',  hint: 'TV na parede ou móvel — formato comum' },
    { val: 'portrait',  label: 'Em pé',    hint: 'TV girada 90° — formato vertical/totem' },
  ];

  return (
    <div className={`grid grid-cols-2 gap-${compact ? '2' : '3'}`}>
      {opts.map((o) => {
        const active = value === o.val;
        return (
          <button
            key={o.val}
            type="button"
            onClick={() => onChange(o.val)}
            className={`relative ${pad} rounded-lg border-2 transition-all text-left ${
              active
                ? 'border-brand-500 bg-brand-50 dark:bg-brand-950/40 shadow-sm'
                : 'border-border bg-background hover:border-brand-300 hover:bg-muted/60'
            }`}
            aria-pressed={active}
          >
            {active && (
              <span className="absolute top-1.5 right-1.5 inline-flex items-center justify-center h-5 w-5 rounded-full bg-brand-500 text-white">
                <Check className="h-3 w-3" />
              </span>
            )}
            <div className="flex items-center gap-3">
              <TvShape
                orientation={o.val}
                className={`${iconSize} ${active ? 'text-brand-600' : 'text-muted-foreground'}`}
              />
              <div className="min-w-0">
                <div className={`${labelSize} font-semibold`}>{o.label}</div>
                {!compact && <div className="text-xs text-muted-foreground mt-0.5">{o.hint}</div>}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function SortableSlideRow({ slide, selected, onClick, onDelete }: any) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: slide.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const TypeIcon = SLIDE_TYPES.find(t => t.value === slide.type)?.icon || ImageIcon;
  const schedule = parseSchedule(slide.schedule);
  const state = scheduleState(schedule);
  // Expirado/futuro fica esmaecido: dá pra bater o olho na playlist e ver o que
  // está vivo. Nada é apagado — quem limpa é o usuário.
  const dimmed = state.kind === 'expired' || state.kind === 'upcoming';
  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={onClick}
      title={state.kind === 'always' ? undefined : describeSchedule(schedule)}
      className={`group flex items-center gap-2 rounded-md p-2 cursor-pointer ${selected ? 'bg-brand-100 border border-brand-300' : 'hover:bg-muted'} ${dimmed ? 'opacity-50' : ''}`}
    >
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing"><GripVertical className="h-4 w-4 text-muted-foreground" /></button>
      <TypeIcon className="h-4 w-4 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{slide.name || `Slide ${slide.order_index}`}</div>
        <div className="text-xs text-muted-foreground flex items-center gap-1">
          {state.kind !== 'always' && <CalendarClock className="h-3 w-3 text-brand-500" />}
          <span className="truncate">{slide.type}</span>
        </div>
      </div>
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        <Clock className="h-3.5 w-3.5" />
        <span>{Math.round(slide.duration_ms / 1000)}s</span>
      </div>
      <button onClick={e => { e.stopPropagation(); onDelete(); }} className="opacity-0 group-hover:opacity-100">
        <Trash2 className="h-4 w-4 text-muted-foreground hover:text-red-500" />
      </button>
    </div>
  );
}

function getDefaultContent(type: string): string {
  const defaults: Record<string, any> = {
    image: { url: '', fit: 'contain' },
    video: { url: '', mute: true, loop: false },
    price_table: { layout: 'grid_3', filter: { only_promo: false }, limit: 12, show_image: true },
    promo_banner: {
      template: 'spotlight',
      headline: 'OFERTA',
      subtitle: '',
      price: '',
      price_old: '',
      cta: '',
      validity: '',
      icon: 'fire',
      background_color: '#C81E1E',
      text_color: '#FFFFFF',
      accent_color: '#FFD700',
    },
    product_carousel: { filter: {}, limit: 30, items_visible: 6, transition_speed_ms: 5000 },
    html: { html: '<div style="display:flex;align-items:center;justify-content:center;height:100%;font-size:64px">Hello</div>' },
    weather: { city: 'São Paulo', layout: 'compact' },
    datetime: { style: 'large', theme: 'dark', show_date: true, show_seconds: true, show_logo: false, logo_source: 'company' },
    football: { view: 'rotate', theme: 'dark', show_clock: true },
  };
  return JSON.stringify(defaults[type] || {});
}
